import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import { StatusCode } from "api/protocols";
import { GetEmailTemplate, GetEmailTemplateVariables, IEmailTemplate, IEmailTemplateVariable, PostEmailTemplate, PostPreviewEmail, TEmailTemplateType } from "api/rpc/2024-04/facilityAdmin/facility/emailTemplate";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import TextEditor from "components/textEditor/textEditor";
import { Select } from "components/select";
import { useTranslation } from "react-i18next";
import { Badge } from "components/badge/Badge";
import axios, { CancelToken } from "axios";
import ReactDOM from "react-dom";
import { faCalendar, faClock, faDollar, faLock, faTicket, faUserGroup } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CustomIcon } from "components/customIcon/CustomIcon";
import "./emailTemplates.scss";
import Spin from "components/spin/spin";

interface INewEmailTemplateState {
  title: string;
  subject: string;
  content: string;
  type: TEmailTemplateType;
}

const AUTOMATED_EMAILS = ["tee_sheet_booking_confirmation", "tee_sheet_booking_cancellation", "reservation_booking_confirmation", "reservation_booking_cancellation" ,"welcome_email", "no_show_email", "birthday_request_email"];
const PROFILE_EMAILS = ["welcome_email", "birthday_request_email"];

export default function EmailTemplateNew() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [automatedTemplates, setAutomatedTemplates] = useState<Array<IEmailTemplate>>([]);
  const [emailVariables, setEmailVariables] = useState<Array<IEmailTemplateVariable>>([]);
  const [newTemplateState, setNewTemplateState] = useState<INewEmailTemplateState>({
    title: "",
    subject: "",
    content: "",
    type: "tee_sheet",
  });
  const [previewEmailHTML, setPreviewEmailHTML] = useState<string>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTemplates(source.token);

    return () => source.cancel();
  }, []);

  useEffect(() => {
    if (!newTemplateState?.content) {
      setPreviewEmailHTML(null);
      return;
    }

    const timeout = setTimeout(() => {
      void previewEmail();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [newTemplateState.content, newTemplateState?.type]);

  const templateTypes = [
    { label: t("Tee Sheet"), value: "tee_sheet" },
    {
      label: t("League Announcement"),
      value: "league_announcement",
    },
    {
      label: t("Tee Time Booking Confirmation"),
      value: "tee_sheet_booking_confirmation",
    },
    {
      label: t("Tee Time Booking Cancellation"),
      value: "tee_sheet_booking_cancellation",
    },
    {
      label: t("Reservation Booking Confirmation"),
      value: "reservation_booking_confirmation",
    },
    {
      label: t("Reservation Booking Cancellation"),
      value: "reservation_booking_cancellation",
    },
    {
      label: t("Welcome Email"),
      value: "welcome_email",
    },
    {
      label: t("No Show Email"),
      value: "no_show_email",
    },
    {
      label: t("Birthday Request Email"),
      value: "birthday_request_email",
    },
  ];

  async function loadTemplates(cancelToken: CancelToken) {
    const [automatedTemplatesRes, variablesRes] = await Promise.all([GetEmailTemplate({ automated: true}, false, cancelToken), GetEmailTemplateVariables(false, cancelToken)]);

    if (automatedTemplatesRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken.reason) {
        return;
      }
      dispatch(showError(t("Error loading automated email templates")));
      return;
    }

    if (variablesRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken.reason) {
        return;
      }
      dispatch(showError(t("Error loading template variables")));
      return;
    }

    const automatedTemplates = automatedTemplatesRes?.data;

    ReactDOM.unstable_batchedUpdates(() => {
      setAutomatedTemplates(automatedTemplates);
      setEmailVariables(variablesRes?.data);
    });
  }

  async function postTemplate() {
    const templateRes = await PostEmailTemplate(
      {
        title: newTemplateState.title,
        type: newTemplateState.type,
        subject: newTemplateState.subject,
        content: newTemplateState.content,
        automated: AUTOMATED_EMAILS.includes(newTemplateState?.type),
      },
      true,
    );

    if (templateRes.status !== StatusCode.OK) {
      dispatch(showError(typeof templateRes?.data === "string" ? templateRes?.data : t("Error creating email template")));
      return;
    }

    dispatch(showSuccess(t("Email template created successfully")));
    history.push("/admin/settings/email-template");
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setNewTemplateState(prev => ({ ...prev, [id]: value }));
  }

  async function previewEmail() {
    //loader
    if (previewEmailHTML !== undefined) {
      setPreviewEmailHTML(undefined);
    }
    const res = await PostPreviewEmail({content: newTemplateState?.content, subject: newTemplateState?.subject, template_type: newTemplateState?.type}, false);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error previewing email")));
      setPreviewEmailHTML("");
      return;
    }
    setPreviewEmailHTML(res?.data as string);
  }

  const primaryAction = {
    content: t("Create"),
    action: postTemplate,
  };

  return (
    <Page
      title={t("New Email Template")}
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to email templates"),
          url: "/admin/settings/email-template",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={newTemplateState.title}
                label={t("Title")}
                id="title"
                onChange={handleInputChange}
              />
              <Input
                value={newTemplateState.subject}
                label={t("Subject")}
                id="subject"
                onChange={handleInputChange}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                defaultValue={newTemplateState.type}
                label={t("Type")}
                onChange={value => setNewTemplateState(prev => ({ ...prev, type: value }))}
              >
                {templateTypes?.filter(type => !automatedTemplates?.some(template => template?.type === type?.value))?.map(type => (
                  <Select.Option key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      {type.label}
                      {AUTOMATED_EMAILS.includes(type.value) ? <Badge type="gray" size="small">{t("Automated")}</Badge> : null}
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group alignStretch>
              <TextEditor
                markdownText={newTemplateState.content ?? ""}
                markdownTextOnChange={(newContent: string) =>
                  setNewTemplateState(prev => ({ ...prev, content: newContent }))
                }
                label={t("Content")}
                variables={AUTOMATED_EMAILS.includes(newTemplateState?.type) ? emailVariables : undefined}
              />
              {previewEmailHTML === undefined ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex justify-center items-center flex-column">
                    <div style={{width: "70px"}}>
                      <Spin/>
                    </div>
                    <p>Loading preview...</p>
                    </div>
                  </div>
              ) : previewEmailHTML === null ? (
                <div>
                  <span className="ui-email-template-preview-label">{t("Preview")}</span>
                </div>
              ) : (
                <div style={{height: "100%"}}>
                  <span className="ui-email-template-preview-label">{t("Preview")}</span>
                <div className="ui-email-template-preview-email">
                  <iframe srcDoc={ 
                    //Don't allow links to be clickable in the preview
                    `
                    <style>
                      a {
                        pointer-events: none;
                        cursor: default;
                      }
                    </style>
                    ${previewEmailHTML}
                    `} style={{width: "70%"}} />
                </div>
                </div>
              )}
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>

        {AUTOMATED_EMAILS.includes(newTemplateState?.type) && !PROFILE_EMAILS.includes(newTemplateState?.type) ? (
           <div className="ui-email-template-booking-details">
           <div className="ui-email-template-booking-details-header">
             <p>{t("Booking Details")}</p>
             <Badge size="small" type="gray"><FontAwesomeIcon icon={faLock} className="mr-1"/> {t("Locked")}</Badge>
           </div>
           <div className="ui-email-template-booking-details-content">
               <div className="ui-email-template-booking-details-content-details">
                 <FontAwesomeIcon icon={faTicket} className="ui-email-template-booking-details-content-details-icon"/>
                 <p>{t("Booking Code")}</p>
               </div>
               <div className="ui-email-template-booking-details-content-divider"></div>
               <div className="ui-email-template-booking-details-content-details">
                 <FontAwesomeIcon icon={faCalendar} className="ui-email-template-booking-details-content-details-icon"/>
                 <p>{t("Date")}</p>
               </div>
               <div className="ui-email-template-booking-details-content-divider"></div>
               <div className="ui-email-template-booking-details-content-details">
                 <FontAwesomeIcon icon={faClock} className="ui-email-template-booking-details-content-details-icon"/>
                 <p>{t("Time")}</p>
               </div>
               <div className="ui-email-template-booking-details-content-divider"></div>
               <div className="ui-email-template-booking-details-content-details">
                 <FontAwesomeIcon icon={faUserGroup} className="ui-email-template-booking-details-content-details-icon"/>
                 <p>{t("Players")}</p>
               </div>
               <div className="ui-email-template-booking-details-content-divider"></div>
               <div className="ui-email-template-booking-details-content-details">
                 <CustomIcon src="/public/images/powercart2-01.svg" size="medium" colour="gray" />
                 <p>{t("Cart")}</p>
               </div>
               <div className="ui-email-template-booking-details-content-divider"></div>
               <div className="ui-email-template-booking-details-content-details">
                 <FontAwesomeIcon icon={faDollar} className="ui-email-template-booking-details-content-details-icon"/>
                 <p>{t("Price")}</p>
               </div>
           </div>
         </div>
        ) : null}
      </Card>
    </Page>
  );
}
